import { useEffect } from 'react';
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider, Outlet } from 'react-router-dom';
import { getPermissionConfig } from './services/permissions/utils';
import { AlertProvider } from './context/alert';
import AlertBanner from './components/common/AlertBanner';
import AuthRoute from './components/common/AuthRoute';
import RoleBasedRedirect from './components/common/RoleBasedRedirect';
import { TryRefreshJWT } from './components/auth/TryRefreshJWT';
import ProtectedRoute from './components/common/ProtectedRoute';
import { useTextReplacer } from './hooks/useTextReplacer';
import pages from './pages';

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      {/*РЕДИРЕКТ ПО РОЛИ*/}
      <Route path="/" element={<RoleBasedRedirect />} />

      {/*ОБЩИЕ*/}
      <Route
        path='/sign_in/login_password'
        element={
          <AuthRoute>
            <pages.AuthPage />
          </AuthRoute>
        }
      />
      <Route
        path='/sign_in/refresh_token'
        element={
          <TryRefreshJWT />
        }
      />

      {/*АДМИНИСТРАТОРСКИЕ*/}
      <Route
        path='/ads'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_ADS').roles}
            requiredPermissions={getPermissionConfig('VIEW_ADS').permissions}
          >
            <pages.AdsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/ads/ad'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_AD').roles}
            requiredPermissions={getPermissionConfig('VIEW_AD').permissions}
          >
            <pages.AdPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/ads/faq'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_ADS_FAQ').roles}
            requiredPermissions={getPermissionConfig('VIEW_ADS_FAQ').permissions}
          >
            <pages.AdsFaqPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/forums'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_FORUMS').roles}
            requiredPermissions={getPermissionConfig('VIEW_FORUMS').permissions}
          >
            <pages.ForumsPages />
          </ProtectedRoute>
        }
      />
      <Route
        path='/forums/forum'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_FORUM').roles}
            requiredPermissions={getPermissionConfig('VIEW_FORUM').permissions}
          >
            <pages.ForumPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/forums/forumComments'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_FORUM_COMMENTS').roles}
            requiredPermissions={getPermissionConfig('VIEW_FORUM_COMMENTS').permissions}
          >
            <pages.ForumComments />
          </ProtectedRoute>
        }
      />
      <Route
        path='/notifications'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_NOTIFICATIONS').roles}
            requiredPermissions={getPermissionConfig('VIEW_NOTIFICATIONS').permissions}
          >
            <pages.NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/help'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_HELP').roles}
            requiredPermissions={getPermissionConfig('VIEW_HELP').permissions}
          >
            <pages.HelpPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/logs'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_LOGS').roles}
            requiredPermissions={getPermissionConfig('VIEW_LOGS').permissions}
          >
            <pages.LogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/metrics'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_METRICS').roles}
            requiredPermissions={getPermissionConfig('VIEW_METRICS').permissions}
          >
            <pages.MetricsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/scoring'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_METRICS').roles}
            requiredPermissions={getPermissionConfig('VIEW_METRICS').permissions}
          >
            <pages.ScoringPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/moderators'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_MODERATORS').roles}
            requiredPermissions={getPermissionConfig('VIEW_MODERATORS').permissions}
          >
            <pages.ModeratorsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/sections'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_SECTIONS').roles}
            requiredPermissions={getPermissionConfig('VIEW_SECTIONS').permissions}
          >
            <pages.SectionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/catalogs'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_CATALOGS').roles}
            requiredPermissions={getPermissionConfig('VIEW_CATALOGS').permissions}
          >
            <pages.CatalogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/catalogs/ads'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_AD').roles}
            requiredPermissions={getPermissionConfig('VIEW_AD').permissions}
          >
            <pages.AdsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/users'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_USERS').roles}
            requiredPermissions={getPermissionConfig('VIEW_USERS').permissions}
          >
            <pages.UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/users/user'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_USER').roles}
            requiredPermissions={getPermissionConfig('VIEW_USER').permissions}
          >
            <pages.UserPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/users/user/advertisements'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_USER_ADS').roles}
            requiredPermissions={getPermissionConfig('VIEW_USER_ADS').permissions}
          >
            <pages.UserAnnouncementsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/users/appeals'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_USER_APPEALS').roles}
            requiredPermissions={getPermissionConfig('VIEW_USER_APPEALS').permissions}
          >
            <pages.AppealsUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/users/suggestions'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_USER_SUGGESTIONS').roles}
            requiredPermissions={getPermissionConfig('VIEW_USER_SUGGESTIONS').permissions}
          >
            <pages.SuggestionsUserPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/users/complaints'
        element={
          <ProtectedRoute
            allowedRoles={getPermissionConfig('VIEW_USER_COMPLAINTS').roles}
            requiredPermissions={getPermissionConfig('VIEW_USER_COMPLAINTS').permissions}
          >
            <pages.ComplaintsUsersPage />
          </ProtectedRoute>
        }
      />
    </Route>
  )
);

function RootLayout() {
  return (
    <AlertProvider>
      <AlertBanner />
      <Outlet />
    </AlertProvider>
  );
}

export const App = () => {
  // Библиотека не локализуется сама, поэтому ищем ее элементы UI на англ
  useTextReplacer();

  /* Fix Panel dropdown in modal*/
  useEffect(() => {
    if (!document.getElementById('dropdown-portal-root')) {
      const dropdownRoot = document.createElement('div');
      dropdownRoot.id = 'dropdown-portal-root';
      dropdownRoot.style.zIndex = '60';
      dropdownRoot.style.position = 'relative';
      document.body.appendChild(dropdownRoot);
    }
  }, []);

  return (
    <div className='App'>
      <RouterProvider router={router} />
    </div>
  );
};
